import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../store';
import { fetchOrgDefaultCurrency, updateOrgDefaultCurrency } from '../store/actions/economicsActions';

/**
 * Org default currency (#2246), cached in Redux so the matter form, billing-rate
 * panel and Organization Settings share one fetch.
 *
 * - `defaultCurrency`: lowercase ISO 4217 (e.g. "usd") or null if unset/unloaded.
 * - `isLoading`: true until the first fetch resolves.
 * - `isFirmAdmin`: whether the current user may change it (PUT is admin-only).
 * - `save(code)`: PUT a new default (firm admin); updates the cache + toasts.
 */
export function useOrgDefaultCurrency() {
  const dispatch = useAppDispatch();
  const value = useSelector((s: RootState) => s.orgDefaultCurrency?.value ?? null);
  const loaded = useSelector((s: RootState) => s.orgDefaultCurrency?.loaded ?? false);
  const userInfo = useSelector((s: RootState) => s.user.userInfo);
  const isFirmAdmin = !!userInfo?.is_org_admin;

  useEffect(() => {
    if (!loaded) {
      dispatch(fetchOrgDefaultCurrency());
    }
  }, [loaded, dispatch]);

  const save = (newCurrency: string) => dispatch(updateOrgDefaultCurrency(newCurrency));

  return { defaultCurrency: value, isLoading: !loaded, isFirmAdmin, save };
}

export default useOrgDefaultCurrency;
